'use client';

import { useRef, useState } from 'react';
import { sendContactEmail } from '@/app/contact/actions';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

type ContactFormProps = {
  requestType?: 'general' | 'booking';
  sourcePage?: string;
  defaultCoverage?: string;
}

const COVERAGE_OPTIONS = [
  'Dedicated athlete',
  'Shared athlete coverage',
  'Full team coverage',
  'Tournament coverage',
  'Season athlete coverage',
  'Full team season',
  'JV + Varsity program season',
  'Custom request',
]

export function ContactForm({
  requestType = 'general',
  sourcePage = 'Contact page',
  defaultCoverage = '',
}: ContactFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const isBooking = requestType === 'booking';
  const selectedCoverage = COVERAGE_OPTIONS.includes(defaultCoverage) ? defaultCoverage : '';

  async function handleSubmit(formData: FormData) {
    setPending(true);
    setMessage(null);

    const result = await sendContactEmail(formData);

    if (result?.error) {
      setMessage({ type: 'error', text: result.error });
    } else {
      setMessage({ type: 'success', text: isBooking ? 'Coverage request sent.' : 'Message sent.' });
      formRef.current?.reset();
    }
    setPending(false);
  }

  return (
    <div className="w-full rounded-xl border border-border bg-card p-6 text-left shadow-sm md:p-8">
      <form ref={formRef} action={handleSubmit} className="space-y-6">
        <input type="hidden" name="requestType" value={requestType} />
        <input type="hidden" name="sourcePage" value={sourcePage} />

        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Your name"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="name@example.com"
          />
        </div>

        {isBooking && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="coverageType" className="text-sm font-medium leading-none">
                Coverage type
              </label>
              <select
                id="coverageType"
                name="coverageType"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                defaultValue={selectedCoverage}
              >
                <option value="" disabled>Select coverage</option>
                {COVERAGE_OPTIONS.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="sport" className="text-sm font-medium leading-none">
                Sport
              </label>
              <input
                type="text"
                id="sport"
                name="sport"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Sport or activity"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="eventDate" className="text-sm font-medium leading-none">
                Event date
              </label>
              <input
                type="date"
                id="eventDate"
                name="eventDate"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="startTime" className="text-sm font-medium leading-none">
                Start time
              </label>
              <input
                type="time"
                id="startTime"
                name="startTime"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="venue" className="text-sm font-medium leading-none">
                Venue
              </label>
              <input
                type="text"
                id="venue"
                name="venue"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="School, field, gym, or city"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="teamOrAthlete" className="text-sm font-medium leading-none">
                Team or athlete
              </label>
              <input
                type="text"
                id="teamOrAthlete"
                name="teamOrAthlete"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Team, athlete, or program"
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="message" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {isBooking ? 'Coverage notes' : 'Message'}
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={isBooking ? 'Schedule notes, priorities, jersey numbers, roster details, or any must-have moments.' : 'How can I help you?'}
          />
        </div>

        {message && (
          <div className={`p-3 rounded-md text-sm ${message.type === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
            {message.text}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            isBooking ? 'Send coverage request' : 'Send message'
          )}
        </Button>
      </form>
    </div>
  );
}
